import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faCheck, faRotateLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import type { InspectionQueueItem } from '@/services/dashboard.service';
import {
  ReturnToEngineerDialog,
  type ReturnToEngineerTarget,
} from '@/features/operations/components/ReturnToEngineerDialog';
import { toast, swalConfirm } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';
import { useAuthStore } from '@/stores/auth.store';

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'Completed',
  REVIEWED: 'Reviewed',
};

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function daysWaiting(completedAt: string | null | undefined): number | null {
  if (!completedAt) return null;
  const ms = Date.now() - new Date(completedAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

interface OperationsReviewsTableProps {
  items: InspectionQueueItem[];
}

export function OperationsReviewsTable({ items }: OperationsReviewsTableProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roleSlug = useAuthStore((s) => s.user?.roleSlug);
  const [returnTarget, setReturnTarget] = useState<ReturnToEngineerTarget | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['dashboard-operations'] });

  const approveMutation = useMutation({
    mutationFn: (id: string) => inspectionService.operationsApprove(id),
    onSuccess: () => {
      toast.success('Approved for commercial');
      setApprovingId(null);
      invalidate();
    },
    onError: (e: Error) => {
      toast.error(extractMsg(e, 'Failed to approve'));
      setApprovingId(null);
    },
  });

  const handleApprove = async (item: InspectionQueueItem) => {
    const ok = await swalConfirm({
      title: 'Approve for Commercial?',
      text: `This will mark inspection ${item.inspectionNumber} as approved and move it to commercial review.`,
      confirmText: 'Approve',
    });
    if (ok) {
      setApprovingId(item.id);
      approveMutation.mutate(item.id);
    }
  };

  // OPS gets the purpose-built Review Workspace (Feature 3); every other role
  // reachable here (Admin, Owner) keeps the existing generic detail page —
  // mirroring the exact branch already established on the Work Queue's Open action.
  const goToDetail = (id: string) =>
    navigate(
      roleSlug === 'operations-user'
        ? ROUTES.OPS_REVIEW_WORKSPACE.replace(':inspectionId', id)
        : ROUTES.INSPECTION_DETAIL.replace(':id', id),
    );

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Pending OPS Reviews</h3>
        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-8 text-sm text-muted-foreground text-center">
          No inspections awaiting review.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                <th className="px-5 py-2.5">Job Number</th>
                <th className="px-5 py-2.5">Customer</th>
                <th className="px-5 py-2.5 hidden sm:table-cell">Equipment</th>
                <th className="px-5 py-2.5 hidden md:table-cell">Engineer</th>
                <th className="px-5 py-2.5 hidden lg:table-cell">Received</th>
                <th className="px-5 py-2.5 text-right hidden lg:table-cell">Days Waiting</th>
                <th className="px-5 py-2.5 hidden md:table-cell">Priority</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => {
                const waiting = daysWaiting(item.completedAt);
                const isApproving = approvingId === item.id && approveMutation.isPending;
                return (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className="px-5 py-3 font-medium text-primary">{item.inspectionNumber}</td>
                    <td className="px-5 py-3 text-foreground">
                      {item.customerOrganizationName ?? item.customerName ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-muted-foreground hidden sm:table-cell">
                      {item.equipmentName ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-muted-foreground hidden md:table-cell">
                      {item.engineerName ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-muted-foreground hidden lg:table-cell">
                      {fmtDate(item.completedAt)}
                    </td>
                    <td className="px-5 py-3 text-right text-muted-foreground hidden lg:table-cell">
                      {waiting !== null ? `${waiting}d` : '—'}
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      {item.priorityName ? (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                          style={{
                            backgroundColor: `${item.priorityColor ?? '#6B7280'}1A`,
                            color: item.priorityColor ?? '#6B7280',
                          }}
                        >
                          {item.priorityName}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center rounded-full bg-teal-500/10 px-2 py-0.5 text-xs font-medium text-teal-700">
                        {STATUS_LABEL[item.status] ?? item.status}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => goToDetail(item.id)}
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label="View"
                          title="View"
                        >
                          <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => void handleApprove(item)}
                          disabled={isApproving}
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-green-700 transition-colors hover:bg-green-500/10 disabled:opacity-50"
                          aria-label="Approve"
                          title="Approve"
                        >
                          <FontAwesomeIcon
                            icon={isApproving ? faSpinner : faCheck}
                            className={`h-3.5 w-3.5 ${isApproving ? 'animate-spin' : ''}`}
                          />
                        </button>
                        <button
                          onClick={() => setReturnTarget(item)}
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-red-700 transition-colors hover:bg-red-500/10"
                          aria-label="Return"
                          title="Return"
                        >
                          <FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ReturnToEngineerDialog
        target={returnTarget}
        onClose={() => setReturnTarget(null)}
        onReturned={() => {
          setReturnTarget(null);
          invalidate();
        }}
      />
    </div>
  );
}

export function OperationsReviewsTableSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <div className="h-4 w-40 rounded skeleton-shimmer" />
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-3.5">
            <div className="h-4 w-20 rounded skeleton-shimmer" />
            <div className="h-4 w-28 rounded skeleton-shimmer" />
            <div className="h-4 w-24 rounded skeleton-shimmer hidden sm:block" />
            <div className="h-5 w-16 rounded-full skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
  );
}
